import { triageBookingIntent } from './logic';

function toIsoDate(date) {
  return date.toISOString().slice(0, 10);
}

export function createMessage(payload) {
  return {
    id: crypto.randomUUID(),
    role: 'assistant',
    kind: 'text',
    text: '',
    ...payload
  };
}

export function getQuickReplies(language) {
  return language === 'fr'
    ? ['Prise de sang à domicile', 'Aide après chirurgie', 'Soutien en santé mentale', 'Consultation nutrition']
    : ['At-home bloodwork', 'Help after surgery', 'Mental health support', 'Nutrition consultation'];
}

export function getInitialConversation(language) {
  return [];
}

export function getExampleConversation(language) {
  return language === 'fr'
    ? [
      'Client: J’ai besoin d’une prise de sang à domicile.',
      'Concierge: Je peux vous aider avec ça. Est-ce pour vous ou pour un proche ?',
      'Client: Pour moi.',
      'Concierge: Merci. Comment décririez-vous votre mobilité aujourd’hui ?',
      'Client: Je suis autonome.',
      'Concierge: Parfait. Je recommande un prélèvement sanguin à domicile, avec possibilité d’une visite infirmière si nécessaire.'
    ]
    : [
      'User: I need bloodwork at home.',
      'Concierge: I can help with that. Is this for you or for a loved one?',
      'User: For me.',
      'Concierge: Thanks. How would you describe your mobility right now?',
      'User: Independent.',
      'Concierge: Perfect. Based on that, I recommend at-home bloodwork, with an optional nurse visit if needed.'
    ];
}

export function inferRouteFromNeed(freeText, selectedRoute = '') {
  return triageBookingIntent({ freeText, selectedRoute }).providerRoute;
}

export function getPrompt(key, language, context = {}) {
  const isFrench = language === 'fr';
  const prompts = {
    serviceNeed: {
      key,
      input: 'text',
      label: isFrench ? 'Décrivez votre besoin' : 'Describe your care need',
      text: isFrench
        ? 'Quel service cherchez-vous aujourd’hui ?'
        : 'What kind of care are you looking for today?',
      placeholder: isFrench
        ? 'De quoi avez-vous besoin ?'
        : 'What do you need help with?',
      quickReplies: getQuickReplies(language)
    },
    patient: {
      key,
      input: 'options',
      text: isFrench
        ? 'Est-ce pour vous ou pour un proche ?'
        : 'Is this for you or for a loved one?',
      options: [
        { value: 'self', label: isFrench ? 'Moi' : 'Me' },
        { value: 'loved_one', label: isFrench ? 'Un proche' : 'Loved one' }
      ]
    },
    mobility: {
      key,
      input: 'options',
      text: isFrench
        ? 'Comment décririez-vous la mobilité de la personne qui recevra les soins ?'
        : 'How would you describe the mobility of the person receiving care?',
      options: [
        { value: 'independent', label: isFrench ? 'Autonome' : 'Independent' },
        { value: 'assisted', label: isFrench ? 'Aide légère' : 'Some assistance' },
        { value: 'lifting_required', label: isFrench ? 'Levage requis' : 'Lifting required' },
        { value: 'transfers_required', label: isFrench ? 'Transferts requis' : 'Transfers required' },
        { value: 'bedbound', label: isFrench ? 'Alitée' : 'Bedbound' }
      ]
    },
    caregiverServices: {
      key,
      input: 'multi_select',
      text: isFrench
        ? 'Quels types d’aide à domicile seraient les plus utiles ? Vous pouvez en choisir plusieurs.'
        : 'Which kinds of in-home support would be most helpful? You can choose more than one.',
      options: [
        { value: 'Companionship', label: isFrench ? 'Compagnie' : 'Companionship' },
        { value: 'Meal prep', label: isFrench ? 'Préparation des repas' : 'Meal prep' },
        { value: 'Medication reminders', label: isFrench ? 'Rappels de médicaments' : 'Medication reminders' },
        { value: 'Hygiene support', label: isFrench ? 'Soutien à l’hygiène' : 'Hygiene support' },
        { value: 'Mobility support', label: isFrench ? 'Soutien à la mobilité' : 'Mobility support' }
      ],
      submitLabel: isFrench ? 'Confirmer' : 'Confirm'
    },
    nurseServiceType: {
      key,
      input: 'cards',
      text: isFrench
        ? 'Quel type de service infirmier correspond le mieux à votre besoin ?'
        : 'Which nursing service best matches what you need?',
      options: [
        { value: 'blood_draw', label: isFrench ? 'Prise de sang' : 'Blood draw' },
        { value: 'injection', label: isFrench ? 'Injection' : 'Injection' },
        { value: 'wound_care', label: isFrench ? 'Soins de plaie' : 'Wound care' },
        { value: 'ear_wash', label: isFrench ? 'Lavage d’oreille' : 'Ear wash' },
        { value: 'other_nursing_support', label: isFrench ? 'Autre soin infirmier' : 'Other nursing support' }
      ]
    },
    otherServiceCategory: {
      key,
      input: 'cards',
      text: isFrench
        ? 'Quel type de professionnel vous conviendrait le mieux ?'
        : 'What kind of provider feels like the best fit?',
      options: [
        { value: 'nutrition', label: isFrench ? 'Nutrition' : 'Nutrition' },
        { value: 'therapy', label: isFrench ? 'Santé mentale' : 'Mental health' },
        { value: 'foot_care', label: isFrench ? 'Soins des pieds' : 'Foot care' },
        { value: 'physiotherapy', label: isFrench ? 'Physiothérapie' : 'Physiotherapy' },
        { value: 'other', label: isFrench ? 'Autre' : 'Other' }
      ]
    },
    routeDetails: {
      key,
      input: 'text',
      label: isFrench ? 'Ajoutez quelques détails' : 'Add a few details',
      text: context.route === 'caregiver'
        ? (isFrench
          ? 'Y a-t-il des détails de sécurité, de routine ou de soutien que je devrais connaître avant de recommander des services ?'
          : 'Are there any safety, routine, or support details I should know before I recommend services?')
        : (isFrench
          ? 'Pouvez-vous me donner un peu plus de contexte sur ce besoin ?'
          : 'Can you share a little more context about this need?'),
      placeholder: isFrench
        ? 'Ajoutez ce qui est important pour la visite'
        : 'Add anything important for the visit'
    },
    location: {
      key,
      input: 'text',
      label: isFrench ? 'Lieu' : 'Location',
      text: isFrench
        ? 'Quel code postal ou quel secteur devrais-je utiliser pour le déplacement ?'
        : 'What postal code or area should I use for travel and matching?',
      placeholder: isFrench ? 'Ex. H2X 1Y4' : 'Example: H2X 1Y4'
    },
    schedule: {
      key,
      input: 'options',
      text: isFrench
        ? 'Quel est votre niveau d’urgence ou votre fenêtre idéale ?'
        : 'What timing feels best for this request?',
      options: [
        { value: 'today', label: isFrench ? 'Aujourd’hui' : 'Today' },
        { value: 'tomorrow', label: isFrench ? 'Demain' : 'Tomorrow' },
        { value: 'this_week', label: isFrench ? 'Cette semaine' : 'This week' },
        { value: 'flexible', label: isFrench ? 'Flexible' : 'Flexible' }
      ]
    },
    preferredLanguage: {
      key,
      input: 'options',
      text: isFrench
        ? 'Quelle langue préférez-vous pour le rendez-vous ?'
        : 'Which language would you prefer for the appointment?',
      options: [
        { value: 'French', label: isFrench ? 'Français' : 'French' },
        { value: 'English', label: isFrench ? 'Anglais' : 'English' },
        { value: 'Other', label: isFrench ? 'Autre' : 'Other' }
      ]
    },
    additionalNotes: {
      key,
      input: 'text',
      label: isFrench ? 'Notes additionnelles' : 'Additional notes',
      text: isFrench
        ? 'Souhaitez-vous ajouter une précision avant que je vous propose les meilleurs services ?'
        : 'Would you like to add anything else before I suggest the best service options?',
      placeholder: isFrench
        ? 'Ajoutez un détail utile, ou laissez ce champ vide puis passez'
        : 'Add a helpful detail, or leave this empty and skip',
      allowSkip: true
    },
    beneficiaryName: {
      key,
      input: 'text',
      label: isFrench ? 'Nom du bénéficiaire' : 'Beneficiary name',
      text: isFrench
        ? 'Quel nom dois-je inscrire pour la personne qui recevra les soins ?'
        : 'What name should I place on the booking for the person receiving care?',
      placeholder: isFrench ? 'Nom complet du bénéficiaire' : 'Full beneficiary name'
    },
    contactName: {
      key,
      input: 'text',
      label: isFrench ? 'Nom du contact' : 'Contact name',
      text: context.patient === 'loved_one'
        ? (isFrench ? 'Et quel est votre nom comme personne contact ?' : 'And what is your name as the contact person?')
        : (isFrench ? 'Quel nom dois-je utiliser comme contact pour cette réservation ?' : 'What name should I use as the booking contact?'),
      placeholder: isFrench ? 'Votre nom complet' : 'Your full name'
    },
    email: {
      key,
      input: 'text',
      label: 'Email',
      text: isFrench
        ? 'Quelle adresse courriel dois-je utiliser pour la confirmation ?'
        : 'Which email should I use for the confirmation?',
      placeholder: isFrench ? 'vous@exemple.com' : 'you@example.com'
    },
    phone: {
      key,
      input: 'text',
      label: isFrench ? 'Téléphone' : 'Phone',
      text: isFrench
        ? 'Quel numéro devrions-nous utiliser si le prestataire doit vous joindre ?'
        : 'What phone number should we use if the provider needs to reach you?',
      placeholder: isFrench ? 'Ex. 514-555-0123' : 'Example: 514-555-0123'
    }
  };

  return prompts[key] || null;
}

export function getRouteAcknowledgement(route, freeText, language) {
  const trimmed = String(freeText || '').trim();
  const isFrench = language === 'fr';

  if (route === 'nurse') {
    return isFrench
      ? `D’accord, je peux vous aider avec ça. ${trimmed ? 'Je vais cadrer ce besoin infirmier avec vous.' : ''}`.trim()
      : `Got it, I can help with that. ${trimmed ? 'I will narrow down the nursing need with you.' : ''}`.trim();
  }

  if (route === 'caregiver') {
    return isFrench
      ? `Merci, je vais vous aider à organiser le bon soutien à domicile.`
      : 'Thanks, I will help you line up the right in-home support.';
  }

  return isFrench
    ? 'Parfait, je vais vous aider à trouver le bon professionnel.'
    : 'Perfect, I will help you find the right provider.';
}

export function mapMobilityToAssessment(mobilityStatus) {
  const base = {
    autonomy: 'autonomous',
    mobility: 'independent',
    liftingRequired: false,
    transfersRequired: false,
    repositioningRequired: false,
    heavyHygieneSupport: false
  };

  if (mobilityStatus === 'assisted') {
    return { ...base, autonomy: 'partial_support', mobility: 'assisted' };
  }

  if (mobilityStatus === 'lifting_required') {
    return { ...base, autonomy: 'non_autonomous', mobility: 'assisted', liftingRequired: true };
  }

  if (mobilityStatus === 'transfers_required') {
    return { ...base, autonomy: 'non_autonomous', mobility: 'assisted', transfersRequired: true };
  }

  if (mobilityStatus === 'bedbound') {
    return {
      ...base,
      autonomy: 'non_autonomous',
      mobility: 'bedbound',
      repositioningRequired: true,
      heavyHygieneSupport: true
    };
  }

  return base;
}

export function getMobilityLabel(mobilityStatus, language) {
  const map = {
    independent: language === 'fr' ? 'Autonome' : 'Independent',
    assisted: language === 'fr' ? 'Aide légère' : 'Some assistance',
    lifting_required: language === 'fr' ? 'Levage requis' : 'Lifting required',
    transfers_required: language === 'fr' ? 'Transferts requis' : 'Transfers required',
    bedbound: language === 'fr' ? 'Alitée' : 'Bedbound'
  };

  return map[mobilityStatus] || (language === 'fr' ? 'À préciser' : 'To be confirmed');
}

export function resolveScheduleChoice(value, language) {
  const now = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(now.getDate() + 1);

  const map = {
    today: {
      scheduleDate: toIsoDate(now),
      label: language === 'fr' ? 'Aujourd’hui' : 'Today',
      notes: language === 'fr' ? 'Urgence: aujourd’hui' : 'Urgency: today'
    },
    tomorrow: {
      scheduleDate: toIsoDate(tomorrow),
      label: language === 'fr' ? 'Demain' : 'Tomorrow',
      notes: language === 'fr' ? 'Urgence: demain' : 'Urgency: tomorrow'
    },
    this_week: {
      scheduleDate: '',
      label: language === 'fr' ? 'Cette semaine' : 'This week',
      notes: language === 'fr' ? 'Urgence: cette semaine' : 'Urgency: this week'
    },
    flexible: {
      scheduleDate: '',
      label: language === 'fr' ? 'Flexible' : 'Flexible',
      notes: language === 'fr' ? 'Urgence: flexible' : 'Urgency: flexible'
    }
  };

  return map[value] || map.flexible;
}

export function buildRecommendationCopy({ language, route, intake }) {
  const isFrench = language === 'fr';
  const requestedServices = (intake.requestedServices || []).join(', ');

  if (route === 'nurse') {
    const typeMap = {
      blood_draw: isFrench ? 'un prélèvement sanguin à domicile' : 'at-home bloodwork',
      injection: isFrench ? 'une visite infirmière pour injection' : 'an in-home injection visit',
      wound_care: isFrench ? 'des soins de plaie à domicile' : 'at-home wound care',
      ear_wash: isFrench ? 'un lavage d’oreille à domicile' : 'an at-home ear wash',
      other_nursing_support: isFrench ? 'un soutien infirmier à domicile' : 'in-home nursing support'
    };

    const base = typeMap[intake.nurseServiceType] || (isFrench ? 'un service infirmier à domicile' : 'an in-home nursing service');
    return isFrench
      ? `D’après ce que vous avez partagé, je recommande ${base}${intake.mobilityStatus ? `, adapté à une mobilité ${getMobilityLabel(intake.mobilityStatus, language).toLowerCase()}` : ''}.`
      : `Based on what you shared, I recommend ${base}${intake.mobilityStatus ? `, tailored for ${getMobilityLabel(intake.mobilityStatus, language).toLowerCase()} mobility` : ''}.`;
  }

  if (route === 'caregiver') {
    return isFrench
      ? `D’après ce que vous avez partagé, je recommande un soutien à domicile${requestedServices ? ` axé sur ${requestedServices.toLowerCase()}` : ''}.`
      : `Based on what you shared, I recommend in-home caregiver support${requestedServices ? ` focused on ${requestedServices.toLowerCase()}` : ''}.`;
  }

  const categoryMap = {
    nutrition: isFrench ? 'une consultation en nutrition' : 'a nutrition consultation',
    therapy: isFrench ? 'un service en santé mentale' : 'a mental health service',
    foot_care: isFrench ? 'un service de soins des pieds' : 'a foot care service',
    physiotherapy: isFrench ? 'un service de physiothérapie' : 'a physiotherapy service',
    other: isFrench ? 'une consultation spécialisée' : 'a specialist consultation'
  };

  return isFrench
    ? `D’après ce que vous avez partagé, je recommande ${categoryMap[intake.otherServiceCategory] || categoryMap.other}.`
    : `Based on what you shared, I recommend ${categoryMap[intake.otherServiceCategory] || categoryMap.other}.`;
}

export function buildConversationalSummary({ language, intake, selectedProvider, selectedService, selectedSlot }) {
  return [
    {
      label: language === 'fr' ? 'Service' : 'Service',
      value: selectedService?.name || intake.serviceReason || intake.consultationReason || intake.healthSafetyNotes || (language === 'fr' ? 'À confirmer' : 'To be confirmed')
    },
    {
      label: language === 'fr' ? 'Mobilité' : 'Mobility',
      value: getMobilityLabel(intake.mobilityStatus, language)
    },
    {
      label: language === 'fr' ? 'Notes' : 'Notes',
      value: intake.notes || intake.healthSafetyNotes || intake.serviceReason || intake.consultationReason || (language === 'fr' ? 'Aucune note supplémentaire' : 'No additional notes')
    },
    {
      label: language === 'fr' ? 'Lieu' : 'Location',
      value: intake.postalCode || (language === 'fr' ? 'À confirmer' : 'To be confirmed')
    },
    {
      label: language === 'fr' ? 'Créneau' : 'Time',
      value: selectedSlot?.label || intake.schedulePreferenceLabel || (language === 'fr' ? 'À confirmer' : 'To be confirmed')
    },
    {
      label: language === 'fr' ? 'Prestataire' : 'Provider',
      value: selectedProvider ? `${selectedProvider.first_name} ${selectedProvider.last_name}` : (language === 'fr' ? 'À confirmer' : 'To be confirmed')
    }
  ];
}

export function isValidEmail(value) {
  return /\S+@\S+\.\S+/.test(String(value || '').trim());
}

export function isValidPhone(value) {
  return String(value || '').replace(/[^\d]/g, '').length >= 10;
}
